<template>
  <div class="config-users">
    <div
      v-if="isMasterOrAdmin"
      class="config-users__header"
    >
      <span class="config-users__title">
        {{ $t('components.config.ConfigUsers.label.title') }}
      </span>
      <p class="config-users__description">
        {{ $t('components.config.ConfigUsers.label.description') }}
      </p>

      <div
        class="config-users__input"
      >
        <v-form v-model="isFormValid">
          <v-combobox
            v-model="currentUsersList"
            class="config-users__input-field"
            outlined
            dense
            :rules="emailRules"
            :items="[]"
            :label="$t('components.config.ConfigUsers.label.input')"
            :hint="$t('components.config.ConfigUsers.hint.emptyEmail')"
            multiple
            small-chips
            deletable-chips
            append-icon=""
            :search-input.sync="newUsers"
            @update:search-input="splitUsers"
          />
        </v-form>
        <DsButton
          class="config-users__add-button"
          color="primary"
          :disabled="!hasDisabled"
          @click="addUsers()"
        >
          {{ $t('components.AddCardModal.label.button.confirmButton') }}
        </DsButton>
      </div>
    </div>
    <span class="config-users__title">
      {{ $t('components.config.ConfigUsers.label.paginationTitle') }}
    </span>
    <div class="config-users__users">
      <v-data-table
        class="config-users__data-table"
        :headers="headers"
        :items="usersInDashboard"
        :items-per-page="5"
        :options.sync="pagination"
        hide-default-footer
        :loading-text="$t('components.config.ConfigUsers.notification.loadingData')"
      >
        <template v-slot:[`item.email`]="{ item }">
          <div class="config-users__user">
            <span>{{ item.email }}</span>
            <v-btn
              v-if="isMasterOrAdmin"
              depressed
              fab
              plain
              x-small
              rounded
              @click="deleteUser(item.email)"
            >
              <DsIcon
                size="small"
                name="trash"
              />
            </v-btn>
          </div>
        </template>
      </v-data-table>

      <div
        v-show="hasPagination"
        class="config-users__pagination text-center ds-margin-top-stack--xs"
      >
        <div class="text-right ds-font-size--sm">
          {{ $t('components.PublishSwitch.label.pagination', {
            page: pagination.page,
            size: paginationSize,
            count: usersInDashboard.length
          }) }}
        </div>
        <v-pagination
          v-model="pagination.page"
          circle
          total-visible="5"
          :length="paginationSize"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { trackEventWithUserProperties } from '@/utils/mixpanel';
import formModel from '@/mixins/form-model';
import { DsIcon } from '@ds/icons';
import { DsButton } from '@ds/components';

export default {
  name: 'ConfigUsers',
  components: {
    DsIcon,
    DsButton,
  },
  mixins: [
    formModel,
  ],
  data() {
    return {
      pagination: {
        itemsPerPage: 4,
      },
      isFormValid: false,
    };
  },
  computed: {
    headers() {
      return [
        {
          text: this.$t('components.config.ConfigUsers.label.users'),
          value: 'email',
        },
      ];
    },
    ...mapGetters('dashboard', [
      'activeDashboard',
    ]),
    ...mapState('user', [
      'user',
    ]),
    usersInDashboard() {
      return this.activeDashboard.data.users || [];
    },
    userEmailsInDashboard() {
      return this.activeDashboard.data.userEmails || [];
    },
    hasDisabled() {
      const hasUsers = Boolean(this.currentUsersList.length);
      return Boolean(hasUsers && this.isFormValid);
    },
    paginationSize() {
      return Math.ceil(this.usersInDashboard.length / this.pagination.itemsPerPage);
    },
    pages () {
      const hasPagination = Boolean(this.pagination.itemsPerPage && this.usersInDashboard.length !== 0);
      return hasPagination ? this.paginationSize : 0;
    },
    hasPagination() {
      return this.paginationSize > 1;
    },
    isMasterOrAdmin() {
      return Boolean(this.user.master || this.user.isAdmin);
    },
  },
  methods: {
    ...mapActions('dashboard', [
      'updateUserEmails',
      'updateUsers',
    ]),
    ...mapActions('user', [
      'getIsAdmin',
    ]),
    addUsers() {
      this.addNewUserEmails();
      this.addNewUsers();

      trackEventWithUserProperties('ST Settings DashboardUsers Add');

      this.currentUsersList = [];
    },
    addNewUserEmails() {
      const newUserEmails = this.currentUsersList;
      const newUserEmailsList = [...new Set([...this.userEmailsInDashboard, ...newUserEmails])];

      this.updateUserEmails(newUserEmailsList);
    },
    addNewUsers() {
      const newUsersList = [];
      const createdByOnboarding = Boolean(this.activeDashboard.data.createdByAccessPage && this.activeDashboard.data.createdByAccessPage === true);
      const filteredEmails = this.currentUsersList.filter(email => !this.userEmailsInDashboard.includes(email));
      const onboardingStatusTemplate = createdByOnboarding
        ? {
          key: 'TO_DO',
          value: 'Não iniciado',
        }
        : {
          key: 'SKIP',
          value: 'Pular',
        };

      filteredEmails.forEach(user => {
        newUsersList.push({
          admin: false,
          email: user,
          onboardingStatus: onboardingStatusTemplate,
        });
      });

      const newUserEmailsList = [...new Set([...this.usersInDashboard, ...newUsersList])];

      this.updateUsers(newUserEmailsList);
    },
    async deleteUser(value) {
      const isAdmin = await this.getIsAdmin();
      if (this.user.master || isAdmin) {
        this.deleteUserEmails(value);
        this.deleteUsers(value);

        trackEventWithUserProperties('ST Settings DashboardUsers Delete');
      }
    },
    deleteUserEmails(email) {
      const filteredUserEmails = this.userEmailsInDashboard.filter((value) => value !== email);
      this.updateUserEmails(filteredUserEmails);
    },
    deleteUsers(email) {
      const filteredUsers = this.usersInDashboard.filter(obj => {
        return obj.email !== email;
      });
      this.updateUsers(filteredUsers);
    },
  },
};
</script>

<style scoped lang="scss">
  .config-users {
    --config-users-width: 734px;
    --config-users-width-flex: 100%;

    width: var(--config-users-width);
    margin-left: var(--ds-spacing-inline-sm);
    margin-top: 18px;
    padding-bottom: 12px;

    &__title {
      font-weight: var(--ds-font-weight-semibold);
      font-size: var(--ds-font-size-md);
      line-height: var(--ds-font-line-height-slim);
    }

    &__description {
      display: flex;
      align-items: center;
      font-weight: var(--ds-font-weight-regular);
      font-size: var(--ds-font-size-sm);
      line-height: var(--ds-font-line-height-loose);
      margin-top: var(--ds-spacing-inline-nano);
      color: var(--ds-color-text-2);
    }

    &__input {
      width: var(--config-users-width-flex);
      display: flex;
      justify-content: space-between;
      gap: var(--ds-spacing-inline-xs);
      margin-top: var(--ds-spacing-inline-sm);
    }

    &__add-button {
      width: 122px;
      height: 40px;
    }

    &__input-field {
      width: 595px;
    }

    &__data-table {
      width: var(--config-users-width-flex);
    }

    &__users {
      width: var(--config-users-width);
    }

    &__pagination {
      display: flex;
      justify-content: right;
      align-items: center;
    }

    &__user {
      display: flex;
      align-items: center;
      justify-content: space-between;

      &:hover {
        cursor: pointer;
      }
    }
  }

  ::v-deep {
    table {
      width: 734px;
      margin-top: var(--ds-spacing-inline-xs);
      border-radius: var(--ds-spacing-stack-nano);
      border: 1px solid var(--ds-color-silver);
    }

    button {
      border-radius: 50%;
      width: 34px;
      height: 34px;
    }
  }
</style>
